import { type ReactNode } from 'react';
import {
  Archive,
  Check,
  Copy,
  Eye,
  Flame,
  FolderInput,
  Forward,
  Loader2,
  Mail,
  MailOpen,
  MoreVertical,
  PackageSearch,
  RefreshCcw,
  Reply,
  ReplyAll,
  ShieldCheck,
  ShieldOff,
  Sparkles,
  Tag,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ReplyMode } from './MessageMetadata';

/**
 * The one desktop action row for reading surfaces (detail header, embedded
 * thread messages, thread header, sender group header, digest cards). Mirrors
 * the MobileActionBar grammar: archive / delete / mark inline, reply cluster,
 * then a More menu holding move-to-folder and the utility/training actions.
 * Hidden below md — mobile surfaces route through MobileActionBar instead.
 */

type TooltipKey = 'archive' | 'delete' | 'markRead' | 'markUnread' | 'move';

interface ActionButtonsProps {
  emailId: string;
  onArchive: (emailId: string) => void;
  onDelete: (emailId: string) => void;
  /** Omit for surfaces where replying makes no sense (e.g. sender groups). */
  onReply?: (mode: ReplyMode) => void;
  isRead?: boolean;
  onMarkRead?: (emailId: string, read: boolean) => void;
  className?: string;
  archiveDisabled?: boolean;
  deleteDisabled?: boolean;
  markDisabled?: boolean;
  moveDisabled?: boolean;
  /** Swap the icon for a spinner while a surface-level operation runs. */
  archivePending?: boolean;
  deletePending?: boolean;
  /** Tooltip overrides for scoped surfaces ("Archive conversation (3)"). */
  tooltips?: Partial<Record<TooltipKey, string>>;
  // Training props (optional)
  canTrainSpam?: boolean;
  canTrainGood?: boolean;
  isTrainingSpam?: boolean;
  isTrainingGood?: boolean;
  onTrainSpam?: () => void;
  onTrainGood?: () => void;
  onSummarize?: () => void;
  onRehydrate?: (emailId: string) => void;
  rehydrating?: boolean;
  extraActions?: ReactNode;
  onAddLabel?: () => void;
  onCopyAiPayload?: () => void;
  onViewOcrData?: () => void;
  onApproveSender?: () => void;
  onBlockSender?: () => void;
  onMoveToFolder?: () => void;
  /** Link this email to a vendor-order journey (picker + process_email nudge). */
  onAddToOrderJourney?: () => void;
}

const ICON_BUTTON_CLASS = 'h-9 w-9 rounded-full transition-colors';
const PRIMARY_TONE = 'hover:bg-primary/10 hover:text-primary';
const DESTRUCTIVE_TONE = 'hover:bg-destructive/10 hover:text-destructive';
const MUTED_TONE = 'hover:bg-muted/50 hover:text-foreground';

function ActionIconButton({
  icon,
  label,
  onClick,
  disabled,
  tone = PRIMARY_TONE,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(ICON_BUTTON_CLASS, tone)}
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

function ActionDivider() {
  return <div className="h-4 w-px bg-border/60 mx-0.5" />;
}

export function ActionButtons({
  emailId,
  onArchive,
  onDelete,
  onReply,
  isRead,
  className,
  archiveDisabled,
  deleteDisabled,
  markDisabled,
  moveDisabled,
  archivePending,
  deletePending,
  tooltips,
  canTrainSpam,
  canTrainGood,
  isTrainingSpam,
  isTrainingGood,
  onTrainSpam,
  onTrainGood,
  onSummarize,
  onRehydrate,
  rehydrating,
  onMarkRead,
  extraActions,
  onAddLabel,
  onCopyAiPayload,
  onViewOcrData,
  onApproveSender,
  onBlockSender,
  onMoveToFolder,
  onAddToOrderJourney,
}: ActionButtonsProps) {
  const showMarkButton = Boolean(onMarkRead) && isRead !== undefined;
  const showOverflowMenu = Boolean(
    onMoveToFolder ||
      onSummarize ||
      onAddLabel ||
      onRehydrate ||
      onCopyAiPayload ||
      onViewOcrData ||
      onApproveSender ||
      onBlockSender ||
      onAddToOrderJourney ||
      onTrainSpam ||
      onTrainGood,
  );
  const hasPrimaryOverflowActions = Boolean(
    onMoveToFolder || onSummarize || onAddLabel || onRehydrate || onCopyAiPayload || onViewOcrData || onApproveSender || onBlockSender || onAddToOrderJourney,
  );
  const hasTrainingOverflowActions = Boolean(onTrainSpam || onTrainGood);
  const hasTrailingActions = Boolean(extraActions) || showOverflowMenu;

  return (
    // Desktop-only: on mobile every reading surface routes actions through the
    // MobileActionBar instead of inline button rows.
    <div className={cn('hidden md:flex flex-nowrap items-center gap-1', className)}>
      <ActionIconButton
        icon={archivePending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
        label={tooltips?.archive ?? 'Archive'}
        onClick={() => onArchive(emailId)}
        disabled={archiveDisabled || archivePending}
      />
      <ActionIconButton
        icon={deletePending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        label={tooltips?.delete ?? 'Delete'}
        onClick={() => onDelete(emailId)}
        disabled={deleteDisabled || deletePending}
        tone={DESTRUCTIVE_TONE}
      />
      {showMarkButton && (
        <ActionIconButton
          icon={isRead ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
          label={isRead ? tooltips?.markUnread ?? 'Mark as unread' : tooltips?.markRead ?? 'Mark as read'}
          onClick={() => onMarkRead?.(emailId, !isRead)}
          disabled={markDisabled}
        />
      )}

      {onReply && (
        <>
          <ActionDivider />
          <ActionIconButton
            icon={<Reply className="h-4 w-4" />}
            label="Reply"
            onClick={() => onReply('reply')}
          />
          <ActionIconButton
            icon={<ReplyAll className="h-4 w-4" />}
            label="Reply all"
            onClick={() => onReply('replyAll')}
          />
          <ActionIconButton
            icon={<Forward className="h-4 w-4" />}
            label="Forward"
            onClick={() => onReply('forward')}
          />
        </>
      )}

      {hasTrailingActions && <ActionDivider />}
      {extraActions}
      {showOverflowMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(ICON_BUTTON_CLASS, MUTED_TONE)}
              aria-label="More actions"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {onMoveToFolder && (
              <DropdownMenuItem onClick={onMoveToFolder} disabled={moveDisabled} className="gap-2">
                <FolderInput className="h-4 w-4" />
                <span>{tooltips?.move ?? 'Move to folder'}</span>
              </DropdownMenuItem>
            )}
            {onSummarize && (
              <DropdownMenuItem onClick={onSummarize} className="gap-2">
                <Sparkles className="h-4 w-4" />
                <span>AI summary</span>
              </DropdownMenuItem>
            )}
            {onAddLabel && (
              <DropdownMenuItem onClick={onAddLabel} className="gap-2">
                <Tag className="h-4 w-4" />
                <span>Add sender label</span>
              </DropdownMenuItem>
            )}
            {onRehydrate && (
              <DropdownMenuItem
                onClick={() => onRehydrate(emailId)}
                disabled={rehydrating}
                className="gap-2"
              >
                {rehydrating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCcw className="h-4 w-4" />
                )}
                <span>{rehydrating ? 'Re-fetching…' : 'Re-fetch'}</span>
              </DropdownMenuItem>
            )}
            {onCopyAiPayload && (
              <DropdownMenuItem
                onClick={() => {
                  void onCopyAiPayload();
                }}
                className="gap-2"
              >
                <Copy className="h-4 w-4" />
                <span>Copy AI payload</span>
              </DropdownMenuItem>
            )}
            {onViewOcrData && (
              <DropdownMenuItem onClick={onViewOcrData} className="gap-2">
                <Eye className="h-4 w-4" />
                <span>View OCR data</span>
              </DropdownMenuItem>
            )}
            {onAddToOrderJourney && (
              <DropdownMenuItem onClick={onAddToOrderJourney} className="gap-2">
                <PackageSearch className="h-4 w-4" />
                <span>Add to order journey</span>
              </DropdownMenuItem>
            )}
            {onApproveSender && (
              <DropdownMenuItem onClick={onApproveSender} className="gap-2">
                <Check className="h-4 w-4" />
                <span>Approve sender</span>
              </DropdownMenuItem>
            )}
            {onBlockSender && (
              <DropdownMenuItem onClick={onBlockSender} className="gap-2">
                <ShieldOff className="h-4 w-4" />
                <span>Block sender</span>
              </DropdownMenuItem>
            )}
            {hasTrainingOverflowActions && hasPrimaryOverflowActions && <DropdownMenuSeparator />}
            {onTrainSpam && (
              <DropdownMenuItem
                onClick={onTrainSpam}
                disabled={!canTrainSpam || isTrainingSpam}
                className="gap-2"
              >
                {isTrainingSpam ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Flame className="h-4 w-4" />
                )}
                <span>{isTrainingSpam ? 'Training…' : canTrainSpam ? 'Train spam' : 'Spam trained'}</span>
              </DropdownMenuItem>
            )}
            {onTrainGood && (
              <DropdownMenuItem
                onClick={onTrainGood}
                disabled={!canTrainGood || isTrainingGood}
                className="gap-2"
              >
                {isTrainingGood ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShieldCheck className="h-4 w-4" />
                )}
                <span>{isTrainingGood ? 'Training…' : canTrainGood ? 'Train good' : 'Good trained'}</span>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
